import React, { PureComponent } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';

import {
	useAnalyticsEvents,
	type WithAnalyticsEventsProps,
	type CreateUIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import {
	DropdownItem as AkDropdownItem,
	DropdownItemGroup as AkDropdownItemGroup,
} from '@atlaskit/dropdown-menu';

// Helpers / Constants
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import { ViewValues } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import type { ContentPropertiesQueryType } from '@confluence/content-body/entry-points/ContentPropertiesQuery.ts';
import {
	ContentPropertiesQuery,
	ContentPropertiesQueryWithBodyRepresentation,
} from '@confluence/content-body/entry-points/ContentPropertiesQuery.ts';
import type { ContentTypeType } from '@confluence/editor-features/entry-points/types.ts';
import {
	EllipsisClassificationMenuItem,
	useIsDataClassificationEnabledForOrg,
} from '@confluence/data-classification/entry-points/ellipsisClassification.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { getAutoConvertionInfo } from '@confluence/editor-conversion/entry-points/getAutoConvertionInfo.ts';
import { RevertEditorDialog } from '@confluence/editor-conversion/entry-points/RevertEditorDialog.ts';
import { EditorMovePageButtonLoader } from '@confluence/editor-move-page-button';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { expVal, UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	PANEL_MODE_TEMPLATES,
	PANEL_MODE_IMPORT,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { EditLabelDropdownItem } from '@confluence/labels/entry-points/EditLabelDropdownItem.ts';
import { useSwitchToLivePage } from '@confluence/live-pages-features/entry-points/useSwitchToLivePage.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { containsNetworkError } from '@confluence/network';
import { ResolvedInlineCommentsCounterLoader } from '@confluence/resolved-inline-comments-dialog/entry-points/ResolvedInlineCommentsCounter.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { ShortcutVisualizer } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { EDITOR2_SHORTCUTS } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { ViewChangesDialogLoader } from '@confluence/view-changes-dialog';
import { useWebItemLocation } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';

import { CommonDropdownMenu } from './CommonDropdownMenu.tsx';
import { onClickPageRevert } from './onClickPageRevert.ts';
import { i18n as sharedi18n } from './sharedDropdowni18n.ts';
import type { EllipsisDropdownComponentProps } from './types.ts';
import { Buttons } from './types.ts';
import { useCompanyHubSettingsGroupMenuItems } from './useCompanyHubSettingsGroupMenuItems.tsx';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
export const i18n = defineMessages({
	preview: {
		id: 'editor-ellipsis-dropdown.preview',
		description: 'The label of Preview button inside ellipsis dropdown of the editor',
		defaultMessage: 'Preview',
	},
	templateDocs: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.templates',
		description: 'The label of Templates button inside ellipsis dropdown of the editor',
		defaultMessage: 'Templates',
	},
	importDocs: {
		id: 'editor-ellipsis-dropdown.ellipsis.open.ellipsis.import',
		description: 'The label of Import button inside ellipsis dropdown of the editor',
		defaultMessage: 'Import',
	},
	addLabels: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.edit.labels',
		description: 'The label of Add labels button inside ellipsis dropdown of the editor',
		defaultMessage: 'Add labels',
	},
	deleteUnpublishedPage: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.page.delete',
		description:
			'The label of Delete unpublished page button inside ellipsis dropdown of the editor',
		defaultMessage: 'Delete unpublished page',
	},
	deleteUnpublishedBlogpost: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.blogpost.delete',
		description:
			'The label of Delete unpublished blog post button inside ellipsis dropdown of the editor',
		defaultMessage: 'Delete unpublished blog post',
	},
	revert: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.revert',
		description:
			'The label of Delete unpublished blog post button inside ellipsis dropdown of the editor',
		defaultMessage: 'Revert to previous version',
	},
	updateWithoutNotifying: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.update.without.notify',
		description:
			'The label of Update without notifying watchers button inside ellipsis dropdown of the editor',
		defaultMessage: 'Update without notifying watchers',
	},
	// eslint-disable-next-line @atlassian/i18n/require-description
	openTemplateBrowser: {
		id: 'editor-ellipsis-dropdown.open.template.browser',
		defaultMessage: 'Open template browser',
	},
	revertEditor: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.revert.editor',
		description: 'The label to revert the page from Fabric to Tiny',
		defaultMessage: 'Revert to legacy editor',
	},
	undo: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.editor.undo',
		description: 'The label to undo edit changes on the page',
		defaultMessage: 'Undo',
	},
	redo: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.editor.redo',
		description: 'The label to redo edit changes on the page',
		defaultMessage: 'Redo',
	},
	findReplace: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.editor.find.replace',
		description: 'The label to find and replace text on the page',
		defaultMessage: 'Find and replace',
	},
});

const noop = () => {};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MenuItemContainer = styled.span({
	display: 'flex',
	justifyContent: 'space-between',
});

const previewPage = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	onPreviewClicked: (value: boolean) => void,
	contentId: string | undefined,
	contentType: ContentTypeType | null,
) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'previewPage',
			source: 'ellipsisDropdownComponent',
			objectId: contentId,
			objectType: contentType,
		},
	}).fire();

	onPreviewClicked(false);
};

const publishWithoutNotify = (createAnalyticsEvent, onClickPublish, contentId, contentType) => {
	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'publishWithoutNotifyFromOverflow',
			source: 'ellipsisDropdownComponent',
			objectId: contentId,
			objectType: contentType,
		},
	}).fire();

	onClickPublish({ suppressNotifyWatchers: true })();
};

class EllipsisDropdownClass extends PureComponent<
	EllipsisDropdownComponentProps &
		WithAnalyticsEventsProps & {
			addCompanyHubSettingsGroupMenuItems: ReturnType<typeof useCompanyHubSettingsGroupMenuItems>;
			isClassificationEnabled: boolean;
			push: (url: string) => void;
			isInitialMenuOpen: boolean;
			switchToLivePage: () => Promise<void>;
			showCommentsPanel: ({ openWithView }: { openWithView: ViewValues }) => void;
		},
	{
		isMenuOpen: boolean;
		isPageAutoconverted: boolean;
	}
> {
	context: any;
	static displayName = 'EllipsisDropdown';

	static defaultProps = {
		saveDraft: noop,
		isDisabled: false,
		isInitialMenuOpen: false,
	};

	state = {
		isMenuOpen: this.props.isInitialMenuOpen,
		isPageAutoconverted: false,
	};

	componentDidMount() {
		void this.setEditorProperty();
	}

	/**
	 * Set editor property state
	 * @returns {Promise<void>}
	 */
	setEditorProperty = async (): Promise<void> => {
		const { contentId } = this.props;
		const { data: contentPropertiesData } = await queryInterop<ContentPropertiesQueryType>({
			query: fg('confluence_editor_auto-convert_changes')
				? ContentPropertiesQueryWithBodyRepresentation
				: ContentPropertiesQuery,
			variables: { contentId },
		}).catch((e) => {
			if (isUnauthorizedError(e) || containsNetworkError(e)) {
				markErrorAsHandled(e);
			} else {
				getMonitoringClient().submitError(e, {
					attribution: Attribution.BACKBONE,
				});
			}
			return { data: null };
		});
		if (contentPropertiesData) {
			const { editorProperty, isAutoConverted } = getAutoConvertionInfo(
				contentPropertiesData?.content?.nodes?.[0],
			);
			const isNewPage = contentPropertiesData?.content?.nodes?.length === 0;

			this.setState({
				isPageAutoconverted:
					(fg('confluence_editor_auto-convert_changes') ? !!isAutoConverted : !editorProperty) &&
					!isNewPage,
			});
		}
	};

	saveDraftAnd = (afterSaveDraftDispatched) => () => {
		if (this.props.saveDraft) {
			void this.props.saveDraft();
		}
		afterSaveDraftDispatched();
	};

	// when allowed ellipsis buttons is undefined, enable all buttons
	isAllowed = (menuItem: Buttons) => this.props.allowedEllipsisButtons?.includes(menuItem) ?? true;

	handleMenuOpenChange = (attrs) => this.setState({ isMenuOpen: attrs.isOpen });
	/**
	 * Menu items are pushed into the 'items' list which is used to create the '•••' menu
	 * (aka more aka ellipsis aka meatball) for editor pages
	 */
	_getItems(dialogs) {
		const {
			addCompanyHubSettingsGroupMenuItems,
			contentType,
			isDisabled,
			isUnpublishedDraft,
			showRevertDialog,
			spaceKey,
			contentId,
			isContentCreationPanelShown,
			contentCreationPanelMode,
			onClickOpenContentCreationPanel,
			onClickPublish,
			retrieveEditorTitleAndContent,
			onPreviewClicked,
			createAnalyticsEvent,
			isImportDisabled,
			push,
			switchToLivePage,
			showCommentsPanel,
			hasEditPermission,
			editorActions,
		} = this.props;

		if (this.state.isMenuOpen) {
			createAnalyticsEvent?.({
				type: 'sendScreenEvent',
				data: {
					name: 'ellipsisDropdownForMoreItems',
				},
			}).fire();
		}

		// Don't want to trigger exposures when users open the ellipsis dropdown we are
		// just disabling an item that is not required for the experiment
		// The exposure will happen when the user converts a page instead
		const [onEditConvertExp] = UNSAFE_noExposureExp('platform_editor_on_edit_convert');

		const addPreviewMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem
					key="preview"
					onClick={() => {
						previewPage(createAnalyticsEvent!, onPreviewClicked, contentId, contentType);
					}}
					elemAfter={<ShortcutVisualizer shortcut={EDITOR2_SHORTCUTS.PREVIEW} />}
				>
					<FormattedMessage {...i18n.preview} />
				</AkDropdownItem>,
			);
		};

		const showViewChangesDialog = () =>
			dialogs.showDialog(ViewChangesDialogLoader, {
				contentId,
			});

		const addViewChangesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="view-changes" onClick={this.saveDraftAnd(showViewChangesDialog)}>
					<FormattedMessage {...sharedi18n.viewChanges} />
				</AkDropdownItem>,
			);
		};

		// Legacy "Templates and import" combined menu item - only shown when cc_snippets is OFF
		const addTemplateBrowserMenuItem = (menuItems: JSX.Element[]) => {
			const label = isImportDisabled ? i18n.templateDocs : sharedi18n.templateImportDocs;
			menuItems.push(
				<AkDropdownItem
					key="open-template-browser"
					onClick={() => onClickOpenContentCreationPanel?.()}
				>
					<FormattedMessage {...label} />
				</AkDropdownItem>,
			);
		};

		// "Templates" menu item - only shown when cc_snippets is ON
		const addTemplatesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem
					key="open-templates"
					onClick={() => onClickOpenContentCreationPanel?.(PANEL_MODE_TEMPLATES)}
				>
					<FormattedMessage {...i18n.templateDocs} />
				</AkDropdownItem>,
			);
		};

		// "Import" menu item - only shown when cc_snippets is ON
		const addImportMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem
					key="open-import"
					onClick={() => onClickOpenContentCreationPanel?.(PANEL_MODE_IMPORT)}
				>
					<FormattedMessage {...i18n.importDocs} />
				</AkDropdownItem>,
			);
		};

		const addClassifyContentMenuItem = (menuItems: JSX.Element[]) => {
			if (this.props.isClassificationEnabled) {
				menuItems.push(<EllipsisClassificationMenuItem />);
			}
		};

		const addEditLabelsMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<EditLabelDropdownItem contentId={contentId || ''} spaceKey={spaceKey} dialogs={dialogs} />,
			);
		};

		const addMovePageMenuItem = (menuItems, movePageContentId, movePageSpaceKey) => {
			menuItems.push(
				<EditorMovePageButtonLoader
					contentId={movePageContentId}
					spaceKey={movePageSpaceKey}
					appearance="dropdownItem"
					analyticsSource="EllipsisDropdownComponent"
					key="move-page-button"
					retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
					isUnpublishedDraft={!!isUnpublishedDraft}
				/>,
			);
		};

		const addResolvedCommentsMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem
					key="resolved-comments"
					onClick={() => {
						showCommentsPanel({ openWithView: ViewValues.RESOLVED });
					}}
				>
					<MenuItemContainer>
						<FormattedMessage {...sharedi18n.resolvedComments} />{' '}
						<ResolvedInlineCommentsCounterLoader contentId={contentId} />
					</MenuItemContainer>
				</AkDropdownItem>,
			);
		};

		const addDeleteChangesMenuItem = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="delete-changes" onClick={this.saveDraftAnd(showRevertDialog)}>
					{isUnpublishedDraft && contentType === 'page' ? (
						<FormattedMessage {...i18n.deleteUnpublishedPage} />
					) : null}
					{isUnpublishedDraft && contentType !== 'page' ? (
						<FormattedMessage {...i18n.deleteUnpublishedBlogpost} />
					) : null}
					{!isUnpublishedDraft ? <FormattedMessage {...i18n.revert} /> : null}
				</AkDropdownItem>,
			);
		};

		const addSwitchToLiveEditMenuItem = ({
			menuItems: menuItemsArg,
		}: {
			menuItems: React.JSX.Element[];
		}) => {
			if (contentType === 'page') {
				const switchToLiveEditWebItem = this.props.webItems?.find(
					(item) => item.id === 'action-switch-to-live-edit',
				);
				if (!switchToLiveEditWebItem) return;

				menuItemsArg.push(
					<AkDropdownItem key="switch-to-live-edit" onClick={switchToLivePage}>
						{switchToLiveEditWebItem.label}
					</AkDropdownItem>,
				);
			}
		};

		const addPublishGroupMenuItems = (menuItems: JSX.Element[]) => {
			const publishGroupItems = [] as JSX.Element[];

			if (this.isAllowed(Buttons.SWITCH_TO_LIVE_EDIT) && contentId) {
				addSwitchToLiveEditMenuItem({
					menuItems: publishGroupItems,
				});
			}
			if (!isUnpublishedDraft) {
				this.isAllowed(Buttons.PUBLISH_WITHOUT_NOTIFY) &&
					publishGroupItems.push(
						<AkDropdownItem
							key="publish-without-notify"
							isDisabled={isDisabled}
							onClick={() => {
								publishWithoutNotify(createAnalyticsEvent, onClickPublish, contentId, contentType);
							}}
						>
							<FormattedMessage {...i18n.updateWithoutNotifying} />
						</AkDropdownItem>,
					);
			}

			publishGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="publish-group-items" key="publish-group-items">
						<AkDropdownItemGroup>{publishGroupItems}</AkDropdownItemGroup>
					</div>,
				);
			return publishGroupItems.length;
		};

		const addOtherGroupMenuItems = (menuItems: JSX.Element[]) => {
			const otherGroupItems = [] as JSX.Element[];
			this.isAllowed(Buttons.PREVIEW) && addPreviewMenuItem(otherGroupItems);
			if (!isUnpublishedDraft && this.isAllowed(Buttons.VIEW_CHANGES)) {
				addViewChangesMenuItem(otherGroupItems);
			}
			// Show option to expose the page template menu that pops out from the side
			// When cc_snippets is ON, show separate Templates and Import menu items
			// When cc_snippets is OFF, show combined "Templates and import" menu item
			if (this.isAllowed(Buttons.TEMPLATE_BROWSER)) {
				// We show it for unpublished drafts by default, and for published pages only when
				// `cc_snippets` is enabled.
				const isPageWithTemplateAccess =
					contentType === 'page' &&
					(isUnpublishedDraft || expVal('cc_snippets', 'isEnabled', false));

				if (expVal('cc_snippets', 'isEnabled', false)) {
					// Templates: shows when panel closed OR panel in import mode
					const shouldShowTemplatesMenuItem =
						isPageWithTemplateAccess &&
						(!isContentCreationPanelShown || contentCreationPanelMode === PANEL_MODE_IMPORT);

					// Import: shows when panel closed OR panel in templates mode (and import not disabled)
					const shouldShowImportMenuItem =
						isPageWithTemplateAccess &&
						!isImportDisabled &&
						(!isContentCreationPanelShown || contentCreationPanelMode === PANEL_MODE_TEMPLATES);

					if (shouldShowTemplatesMenuItem) {
						addTemplatesMenuItem(otherGroupItems);
					}
					if (shouldShowImportMenuItem) {
						addImportMenuItem(otherGroupItems);
					}
				} else {
					// Legacy "Templates and import" combined menu item - only shown when flag is OFF
					const shouldShowOpenContentCreationMenuItem =
						!isContentCreationPanelShown && isPageWithTemplateAccess;
					if (shouldShowOpenContentCreationMenuItem) {
						addTemplateBrowserMenuItem(otherGroupItems);
					}
				}
			}
			if (contentId && spaceKey) {
				this.isAllowed(Buttons.CLASSIFY_CONTENT) && addClassifyContentMenuItem(otherGroupItems);
				this.isAllowed(Buttons.EDIT_LABELS) && addEditLabelsMenuItem(otherGroupItems);
				this.isAllowed(Buttons.MOVE_PAGE) &&
					addMovePageMenuItem(otherGroupItems, contentId, spaceKey);
				this.isAllowed(Buttons.RESOLVED_COMMENTS) && addResolvedCommentsMenuItem(otherGroupItems);
			}

			// Empty title needed to maintain blank line above group
			otherGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="other-group-items" key="other-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{otherGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const addEditorActionsGroupMenuItems = (menuItems: JSX.Element[]): void => {
			const editorActionsGroupItems = [] as JSX.Element[];

			const isAllowed = (contentType === 'page' || contentType === 'blogpost') && hasEditPermission;

			if (!isAllowed || !editorActions) {
				return;
			}

			const {
				canUndo: editorCanUndo,
				canRedo: editorCanRedo,
				undo: editorUndo,
				redo: editorRedo,
				activateFindReplace: editorActivateFindReplace,
			} = editorActions;

			if (!!editorUndo) {
				editorActionsGroupItems.push(
					<AkDropdownItem
						key="editor-undo"
						isDisabled={!editorCanUndo}
						onClick={() => {
							editorUndo?.();
						}}
						elemAfter={<ShortcutVisualizer shortcut={EDITOR2_SHORTCUTS.UNDO} />}
					>
						<FormattedMessage {...i18n.undo} />
					</AkDropdownItem>,
				);
			}

			if (!!editorRedo) {
				editorActionsGroupItems.push(
					<AkDropdownItem
						key="editor-redo"
						isDisabled={!editorCanRedo}
						onClick={() => {
							editorRedo?.();
						}}
						elemAfter={<ShortcutVisualizer shortcut={EDITOR2_SHORTCUTS.REDO} />}
					>
						<FormattedMessage {...i18n.redo} />
					</AkDropdownItem>,
				);
			}

			if (!!editorActivateFindReplace) {
				editorActionsGroupItems.push(
					<AkDropdownItem
						key="editor-find-replace"
						onClick={() => {
							editorActivateFindReplace?.();
						}}
						elemAfter={<ShortcutVisualizer shortcut={EDITOR2_SHORTCUTS.FIND_REPLACE} />}
					>
						<FormattedMessage {...i18n.findReplace} />
					</AkDropdownItem>,
				);
			}

			editorActionsGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="editor-actions-group-items" key="editor-actions-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{editorActionsGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const showRevertEditorDialog = () => {
			dialogs.showDialog(RevertEditorDialog, {
				contentId,
				onPageRevert: () => onClickPageRevert(push, contentType, spaceKey, contentId),
			});
		};

		const addRevertToLegacyEditor = (menuItems: JSX.Element[]) => {
			menuItems.push(
				<AkDropdownItem key="revert-editor" onClick={this.saveDraftAnd(showRevertEditorDialog)}>
					<FormattedMessage {...i18n.revertEditor} />
				</AkDropdownItem>,
			);
		};

		const addDiscardGroupMenuItems = (menuItems: JSX.Element[]) => {
			const discardGroupItems = [] as JSX.Element[];

			if (this.isAllowed(Buttons.DELETE_DRAFT)) {
				addDeleteChangesMenuItem(discardGroupItems);
				this.state.isPageAutoconverted &&
					(fg('confluence_editor_auto-convert_changes')
						? true
						: !onEditConvertExp.get('isEnabled', false)) &&
					addRevertToLegacyEditor(discardGroupItems);
			}

			// Empty title needed to maintain blank line above group
			discardGroupItems.length !== 0 &&
				menuItems.push(
					<div data-testid="discard-group-items" key="discard-group-items">
						<AkDropdownItemGroup hasSeparator={menuItems.length >= 1}>
							{discardGroupItems}
						</AkDropdownItemGroup>
					</div>,
				);
		};

		const allMenuItems = [] as JSX.Element[];

		/**
		 * Menu items are separated into three groups: publish, other and discard
		 * Use the allowedEllipsisButtons array to filter for buttons needed to be rendered
		 */

		addPublishGroupMenuItems(allMenuItems);
		addEditorActionsGroupMenuItems(allMenuItems);
		addOtherGroupMenuItems(allMenuItems);
		addDiscardGroupMenuItems(allMenuItems);
		addCompanyHubSettingsGroupMenuItems(allMenuItems);

		return allMenuItems;
	}

	render() {
		const { isDisabled, tooltipDisabledState } = this.props;

		return (
			<Subscribe to={[DialogsStateContainer]}>
				{(dialogs) => (
					<div data-testid="ellipsis-menu">
						<CommonDropdownMenu
							isMenuOpen={this.state.isMenuOpen}
							handleMenuOpenChange={this.handleMenuOpenChange}
							isDisabled={isDisabled}
							tooltipDisabledState={tooltipDisabledState}
						>
							{this._getItems(dialogs)}
						</CommonDropdownMenu>
					</div>
				)}
			</Subscribe>
		);
	}
}

export const EllipsisDropdownComponent = withFlags((props: EllipsisDropdownComponentProps) => {
	const addCompanyHubSettingsGroupMenuItems = useCompanyHubSettingsGroupMenuItems(props);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isEnabled: isClassificationEnabled } = useIsDataClassificationEnabledForOrg();
	const { showCommentsPanel } = useShowCommentsPanel();

	const { push } = useRouteActions();
	const webItemLocation = useWebItemLocation({
		location: 'system.content.action',
		contentId: props.contentId,
	});

	const switchToLivePage = useSwitchToLivePage({
		contentId: props.contentId!,
		spaceId: props.spaceId,
		isUnpublishedDraft: !!props.isUnpublishedDraft,
		flags: props.flags,
		retrieveEditorTitleAndContent: props.retrieveEditorTitleAndContent,
		saveDraft: props.saveDraft,
		analyticsSource: 'ellipsisDropdownComponent',
		adfOrigin: 'EDITOR',
		onSuccessfulConversion: () => {
			// Redirect to the route for the new live page.
			push(
				VIEW_PAGE.toUrl({
					spaceKey: props.spaceKey,
					contentId: props.contentId,
				}),
			);
		},
	});

	return (
		<EllipsisDropdownClass
			{...props}
			addCompanyHubSettingsGroupMenuItems={addCompanyHubSettingsGroupMenuItems}
			createAnalyticsEvent={createAnalyticsEvent}
			isClassificationEnabled={isClassificationEnabled}
			push={push}
			webItems={webItemLocation?.webItems || []}
			switchToLivePage={switchToLivePage}
			showCommentsPanel={showCommentsPanel}
		/>
	);
});
